import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, type ChatMessage } from "../api/types";
import { useChat, useSessionRun } from "../store/chat";
import { useReports } from "../store/reports";
import { useLocale } from "../hooks/useLocale";
import { MessageBubble } from "./MessageBubble";
import { SaveModal } from "./SaveModal";
import { StepTimeline } from "./StepTimeline";
import { ClarifyPrompt } from "./ClarifyPrompt";
import { CancelButton } from "./CancelButton";
import styles from "./ChatPanel.module.css";

interface Props {
  onCreateSession: () => Promise<void>;
}

interface OptimisticUser {
  text: string;
  ts: number;
}

export function ChatPanel({ onCreateSession }: Props) {
  const { t, tOr } = useLocale();
  const activeSessionId = useChat((s) => s.activeSessionId);
  const session = useChat((s) =>
    s.sessions.find((x) => x.id === s.activeSessionId) ?? null,
  );
  const messages = useChat((s) => s.messages);
  const loadingDetail = useChat((s) => s.loadingDetail);
  const createFromMessage = useReports((s) => s.createFromMessage);
  const navigate = useNavigate();

  // Per-session run projection; mounting this hook is what keeps the
  // session's server-side run observed (attach/detach follows the component
  // lifecycle, replay cursor lives in the store).
  const run = useSessionRun(activeSessionId);
  const sendMessage = useChat((s) => s.sendMessage);
  const cancelRun = useChat((s) => s.cancelRun);

  const [draft, setDraft] = useState("");
  // The id of the message whose выгрузка we're saving as a report, or null.
  const [saveTarget, setSaveTarget] = useState<number | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [optimisticUser, setOptimisticUser] = useState<OptimisticUser | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the composer textarea to fit the draft, clamped by min/max-height
  // in CSS. Resetting height to "auto" first so it can shrink as well as grow.
  useEffect(() => {
    const ta = composerRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [draft]);

  // Component-local composer state is per-session; run state itself is keyed
  // by session in the store and needs no reset on switch.
  useEffect(() => {
    setOptimisticUser(null);
    setSendError(null);
  }, [activeSessionId]);

  // Clear the optimistic user-bubble when the run reaches a terminal state.
  // The store has already reloaded `messages` (or kept it on error), so the
  // persisted user message replaces the optimistic placeholder.
  useEffect(() => {
    if (
      run.status === "idle" ||
      run.status === "error" ||
      run.status === "cancelled"
    ) {
      setOptimisticUser(null);
    }
  }, [run.status]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight });
  }, [messages.length, run.steps.length, optimisticUser]);

  if (!activeSessionId) {
    return (
      <div className={styles.empty}>
        <p>{t("chat.empty_session")}</p>
        <button
          type="button"
          className={styles.primary}
          onClick={() => void onCreateSession()}
        >
          {t("chat.new_session")}
        </button>
      </div>
    );
  }

  const isStreaming = run.status === "streaming";
  const isAwaitingClarify = run.status === "awaiting_clarify";
  const busy = isStreaming || isAwaitingClarify;

  function buildOptimisticMessage(): ChatMessage | null {
    if (!optimisticUser || !activeSessionId) return null;
    return {
      id: -optimisticUser.ts,
      session_id: activeSessionId,
      tenant_id: 0,
      role: "user",
      content: optimisticUser.text,
      created_at: new Date(optimisticUser.ts).toISOString(),
    };
  }

  function dispatchStream(text: string) {
    if (!activeSessionId) return;
    setSendError(null);
    setOptimisticUser({ text, ts: Date.now() });
    void sendMessage(text);
  }

  function submit() {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft("");
    dispatchStream(text);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    submit();
  }

  async function onSave(name: string) {
    if (saveTarget == null) return;
    try {
      const created = await createFromMessage(saveTarget, name);
      setSaveTarget(null);
      navigate(`/report/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        throw new Error(
          tOr(
            `errors.${err.code}`,
            "errors.unknown",
            err.params as Record<string, string | number> | undefined,
          ),
        );
      }
      throw err;
    }
  }

  async function onCancel() {
    if (!activeSessionId) return;
    try {
      await cancelRun(activeSessionId);
    } catch (err) {
      console.warn("cancel failed", err);
    }
  }

  function onClarifyAnswer(answer: string) {
    if (!activeSessionId || run.status !== "awaiting_clarify") return;
    dispatchStream(answer);
  }

  const optimisticMsg = buildOptimisticMessage();
  const errorNotice =
    run.status === "error"
      ? tOr(`errors.${run.errorCode ?? "unknown"}`, "errors.unknown")
      : null;

  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>
            {session?.name?.trim() || t("chat.untitled", { id: activeSessionId })}
          </h2>
        </div>
      </header>

      <div className={styles.scroll} ref={scrollRef}>
        {loadingDetail && messages.length === 0 ? (
          <p className={styles.loading}>{t("common.loading")}</p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              onSaveAsReport={busy ? undefined : (id) => setSaveTarget(id)}
              saveDisabled={busy}
            />
          ))
        )}

        {optimisticMsg && <MessageBubble message={optimisticMsg} />}

        {(isStreaming || isAwaitingClarify) && (
          <StepTimeline steps={run.steps} active={isStreaming} />
        )}

        {isAwaitingClarify && run.clarifyQuestion && (
          <ClarifyPrompt
            question={run.clarifyQuestion}
            onSubmit={onClarifyAnswer}
          />
        )}

        {run.status === "cancelled" && (
          <p className={styles.statusNotice}>{t("chat.cancelled_notice")}</p>
        )}

        {errorNotice && (
          <p className={`${styles.statusNotice} ${styles.errorNotice}`}>
            {t("chat.error_notice")}: {errorNotice}
          </p>
        )}
      </div>

      <form className={styles.composer} onSubmit={onSubmit}>
        {sendError && <p className={styles.error}>{sendError}</p>}
        <textarea
          ref={composerRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("chat.placeholder")}
          rows={1}
          maxLength={20000}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        {busy ? (
          <CancelButton onCancel={onCancel} />
        ) : (
          <button
            type="submit"
            className={styles.sendBtn}
            disabled={!draft.trim()}
          >
            {t("chat.send")}
          </button>
        )}
      </form>

      {saveTarget != null && (
        <SaveModal
          initialName={session?.name ?? ""}
          onSubmit={onSave}
          onClose={() => setSaveTarget(null)}
        />
      )}
    </div>
  );
}
